import React, { FC, Suspense, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { getDateFnsLocale } from 'utils/format';
import { getDenthubSettingsTemplate } from 'apollo/templates/denthub-settings';
import { AppSettingsProvider } from 'context/app-settings';
import { DataSetSwitchProvider } from 'context/data-set-switch';
import { DenthubSetting } from 'models/denthub-settings';
import { SuperAdmin } from 'models/super-admin';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import AppToaster from 'components/shared/app-toaster/app-toaster.component';
import { AppToasterContent, AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import Loader from 'components/shared/loader/loader.component';
import TestDataBanner from 'components/shared/test-data-banner/test-data-banner.component';
import AdminShell from 'components/navigation/admin-shell/admin-shell.component';
import PageNotFound from './page-not-found.component';
import Users from 'pages/users/users';
import AdminClinics from 'pages/admin/admin-clinics.component';
import AdminDentists from 'pages/admin/admin-dentists.component';
import AdminTechnicians from 'pages/admin/admin-technicians.component';
import AdminLabs from 'pages/admin/admin-labs.component';
import CustomerSettings from 'pages/customer-settings/customer-settings.component';
import AppSettings from 'pages/app-settings/app-settings.component';
import ReleaseNotes from 'pages/release-notes/release-notes.component';
import TransportAdmin from 'pages/transport-admin/transport-admin.component';
import AdminAddTransport from 'pages/transport-admin/admin-add-transport.component';
import Orders from 'pages/orders/orders.component';
import OrderDetails from 'pages/orders/order-details.component';
import Invoices from 'pages/invoices/invoices.component';
import InvoiceDetails from 'pages/invoices/invoice-details.component';
import SupportScreenShares from 'pages/support-screen-share/support-screen-shares.component';
import SupportScreenShareViewer from 'pages/support-screen-share/support-screen-share-viewer.component';

type Props = {
    admin: SuperAdmin;
    onLogout: () => void;
};

/**
 * Every route here is a super admin page; there are no role checks because
 * nobody else can log in to this app (super-admin-backend refuses them).
 */
const App: FC<Props> = ({ admin, onLogout }) => {
    const { showBoundary } = useDenthubErrorBoundary();
    const [denthubSettings, setDenthubSettings] = useState<DenthubSetting>({} as DenthubSetting);
    const [appToasterContent, setAppToasterContent] = useState<AppToasterContent>({ isOpen: false, severity: 'error' });

    const { loading } = useQuery(getDenthubSettingsTemplate, {
        onError: (error) => showBoundary(error),
        onCompleted: ({ denthubSettings: settings }) => setDenthubSettings(settings),
    });

    if (loading) {
        return <Loader />;
    }

    return (
        <AppSettingsProvider value={{ appSettings: denthubSettings, setAppSettings: setDenthubSettings }}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getDateFnsLocale()}>
                <AppToasterContext.Provider value={{ appToasterContent, setAppToasterContent }}>
                    <AppToaster />
                    <DataSetSwitchProvider>
                        <AdminShell admin={admin} onLogout={onLogout}>
                            <TestDataBanner />
                            <Suspense fallback={null}>
                                <Routes>
                                    <Route path="/" element={<Navigate to="/users" replace />} />
                                    <Route path="/users" Component={Users} />
                                    <Route path="/orders" Component={Orders} />
                                    <Route path="/orders/:id" Component={OrderDetails} />
                                    <Route path="/invoices" Component={Invoices} />
                                    <Route path="/invoices/:id" Component={InvoiceDetails} />
                                    <Route path="/admin-clinics" Component={AdminClinics} />
                                    <Route path="/admin-dentists" Component={AdminDentists} />
                                    <Route path="/admin-technicians" Component={AdminTechnicians} />
                                    <Route path="/admin-labs" Component={AdminLabs} />
                                    <Route path="/customer-settings/:id" Component={CustomerSettings} />
                                    <Route path="/app-settings/:tab" Component={AppSettings} />
                                    <Route path="/release-notes" Component={ReleaseNotes} />
                                    <Route path="/admin-transport/:tab" Component={TransportAdmin} />
                                    <Route path="/admin-add-transport/:type" Component={AdminAddTransport} />
                                    <Route path="/screen-share" Component={SupportScreenShares} />
                                    <Route path="/screen-share/:code" Component={SupportScreenShareViewer} />
                                    <Route path="*" Component={PageNotFound} />
                                </Routes>
                            </Suspense>
                        </AdminShell>
                    </DataSetSwitchProvider>
                </AppToasterContext.Provider>
            </LocalizationProvider>
        </AppSettingsProvider>
    );
};

export default App;
